import {
  ActiveUsersExportParams,
  ActiveUsersExportResponse,
  ActiveUsersMetrics,
  ActiveUsersMetricsParams,
} from "../../models/activeUsersMetrics";
import { API_ENDPOINTS } from "../../utils/api-service";
import { parseContentDispositionFilename } from "../../utils/functions/downloadBlob";
import { api } from "./api";

const DEFAULT_ACTIVE_USERS_FILENAME = "usuarios-activos.csv";

const isActiveUsersExportResponse = (
  result: unknown
): result is ActiveUsersExportResponse => {
  if (!result || typeof result !== "object") {
    return false;
  }
  const value = result as { status?: unknown; blob?: unknown };
  return value.status === 204 || (value.status === 200 && value.blob instanceof Blob);
};

const activeUsersListTag = { type: "activityMetrics" as const, id: "ACTIVE_USERS" };

/** Un solo mapeo para la query y el export: si divergen, el CSV deja de coincidir con la pantalla. */
const activeUsersQueryParams = (
  params: ActiveUsersMetricsParams | void
): Record<string, string> => ({
  ...(params?.dateTo ? { date_to: params.dateTo } : {}),
  ...(params?.windowDays ? { window_days: String(params.windowDays) } : {}),
  ...(params?.tenantId ? { tenant_id: params.tenantId } : {}),
});

const unwrapActiveUsersMetrics = (response: unknown): ActiveUsersMetrics => {
  if (typeof response === "object" && response !== null) {
    const record = response as Record<string, unknown>;
    if (record.success === true && record.data !== undefined) {
      return record.data as ActiveUsersMetrics;
    }
  }

  return response as ActiveUsersMetrics;
};

export const activityApi = api.injectEndpoints({
  endpoints: (build) => ({
    getActiveUsersMetrics: build.query<
      ActiveUsersMetrics,
      ActiveUsersMetricsParams | void
    >({
      query: (params) => ({
        url: API_ENDPOINTS.ACTIVITY.ACTIVE_USERS,
        params: activeUsersQueryParams(params),
      }),
      transformResponse: unwrapActiveUsersMetrics,
      // Placeholder al volver a la pantalla; el refetch sigue activo.
      keepUnusedDataFor: 300,
      providesTags: [activeUsersListTag],
    }),

    exportActiveUsers: build.mutation<ActiveUsersExportResponse, ActiveUsersExportParams>({
      query: (params) => ({
        url: API_ENDPOINTS.ACTIVITY.ACTIVE_USERS_EXPORT,
        params: activeUsersQueryParams(params),
        // El responseHandler devuelve el error en vez de tirarlo: si lanza, fetchBaseQuery
        // lo envuelve en PARSING_ERROR y se pierden el status y el mensaje del backend.
        responseHandler: async (response) => {
          if (response.status === 204) {
            return { status: 204 as const };
          }

          const contentType = response.headers.get("Content-Type") || "";

          if (!response.ok) {
            return contentType.includes("json")
              ? ((await response.json()) as unknown)
              : { message: `Error al exportar (${response.status})` };
          }

          if (!contentType.includes("csv") && !contentType.includes("octet-stream")) {
            return { message: `Tipo de contenido inesperado: ${contentType}` };
          }

          return {
            status: 200 as const,
            blob: await response.blob(),
            filename:
              parseContentDispositionFilename(
                response.headers.get("Content-Disposition")
              ) ?? DEFAULT_ACTIVE_USERS_FILENAME,
          };
        },
        validateStatus: (response, result) =>
          response.ok && isActiveUsersExportResponse(result),
      }),
    }),
  }),
});

export const { useGetActiveUsersMetricsQuery, useExportActiveUsersMutation } = activityApi;
